import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { WEEKDAY_LETTER } from '../FinancialCalendar/calendarUtils';
import {
  AddAnnualDateButton,
  AnnualCalendarDay,
  AnnualCalendarGrid,
  AnnualCalendarNav,
  AnnualCalendarPopover,
  AnnualDatePickerAnchor,
  AnnualDateRow,
  AnnualDateTag,
  ChipsGrid,
  DayChip,
  DayPickerWrapper,
} from './style';

/* ── Chip multi-select (Semanal / Mensal) ─────────────────────────── */

interface ChipOption {
  value: number;
  label: string;
}

interface RecurrenceChipPickerProps {
  label: string;
  options: ChipOption[];
  selected: number[];
  onChange: (values: number[]) => void;
  hasError?: boolean;
}

export const RecurrenceChipPicker: React.FC<RecurrenceChipPickerProps> = ({
  label,
  options,
  selected,
  onChange,
  hasError,
}) => {
  const toggle = (value: number) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value].sort((a, b) => a - b));
    }
  };

  return (
    <DayPickerWrapper $hasError={hasError}>
      <label>{label}</label>
      <ChipsGrid>
        {options.map((opt) => (
          <DayChip
            key={opt.value}
            type="button"
            $selected={selected.includes(opt.value)}
            onClick={() => toggle(opt.value)}
          >
            {opt.label}
          </DayChip>
        ))}
      </ChipsGrid>
    </DayPickerWrapper>
  );
};

/* ── Annual date list (Anual) ─────────────────────────────────────── */

/**
 * `dates` é um array de strings no formato "MM-DD" (ex: "03-15").
 */
interface RecurrenceAnnualPickerProps {
  dates: string[];
  onChange: (dates: string[]) => void;
  hasError?: boolean;
}

const MONTH_SHORT = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

const MONTH_LONG = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function formatAnnualDate(mmdd: string): string {
  const [mm, dd] = mmdd.split('-');
  const monthIndex = parseInt(mm, 10) - 1;
  const day = parseInt(dd, 10);
  if (isNaN(monthIndex) || isNaN(day)) return mmdd;
  const monthName = MONTH_SHORT[monthIndex] ?? mm;
  return `${day.toString().padStart(2, '0')}/${monthName}`;
}

function toMonthDay(monthIndex: number, day: number): string {
  return `${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export const RecurrenceAnnualPicker: React.FC<RecurrenceAnnualPickerProps> = ({
  dates,
  onChange,
  hasError,
}) => {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(() => new Date());
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!anchorRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  const handleAddDay = (day: number) => {
    const mmdd = toMonthDay(month, day);
    if (!dates.includes(mmdd)) {
      onChange([...dates, mmdd].sort());
    }
    setOpen(false);
  };

  const handleRemove = (mmdd: string) => {
    onChange(dates.filter((date) => date !== mmdd));
  };

  const shiftMonth = (offset: number) => {
    setCursor(new Date(year, month + offset, 1));
  };

  return (
    <DayPickerWrapper $hasError={hasError} style={{ gridColumn: '1 / -1' }}>
      <label>Datas do ano</label>
      <AnnualDateRow>
        {dates.map((mmdd) => (
          <AnnualDateTag key={mmdd}>
            {formatAnnualDate(mmdd)}
            <button
              type="button"
              onClick={() => handleRemove(mmdd)}
              aria-label={`Remover ${formatAnnualDate(mmdd)}`}
            >
              <X size={12} />
            </button>
          </AnnualDateTag>
        ))}
        <AnnualDatePickerAnchor ref={anchorRef}>
          <AddAnnualDateButton
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <Plus size={14} />
            Adicionar data
          </AddAnnualDateButton>
          {open && (
            <AnnualCalendarPopover role="dialog" aria-label="Escolher data do ano">
              <AnnualCalendarNav>
                <button type="button" onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
                  <ChevronLeft size={16} />
                </button>
                <span>{`${MONTH_LONG[month]} de ${year}`}</span>
                <button type="button" onClick={() => shiftMonth(1)} aria-label="Próximo mês">
                  <ChevronRight size={16} />
                </button>
              </AnnualCalendarNav>
              <AnnualCalendarGrid>
                {WEEKDAY_LETTER.map((letter, index) => (
                  <span key={`${letter}-${index}`}>{letter}</span>
                ))}
                {cells.map((day, index) => {
                  if (day == null) return <span key={`empty-${index}`} />;
                  const selected = dates.includes(toMonthDay(month, day));
                  return (
                    <AnnualCalendarDay
                      key={day}
                      type="button"
                      $selected={selected}
                      onClick={() => handleAddDay(day)}
                    >
                      {day}
                    </AnnualCalendarDay>
                  );
                })}
              </AnnualCalendarGrid>
            </AnnualCalendarPopover>
          )}
        </AnnualDatePickerAnchor>
      </AnnualDateRow>
    </DayPickerWrapper>
  );
};
